<template>
    <cic-popup
        :title="item.templateId ? $t('Edit Template') : $t('New Template')"
        :saveable="editing"
        @blur="$emit('blur')"
        @input="$emit('input')"
    >
        <ul class="item-form">
            <li class="form-label-first">
                {{ $t('Template Name') }}
            </li>
            <li>
                <cic-textbox
                    id="templateName"
                    :enabled="editing"
                    :value="item.templateName"
                    :maxlength="256"
                    @input="(val) => updateFieldValue('templateName', val)"
                />
            </li>

            <li class="form-label">
                {{ $t('Assigned Agent Groups') }}
            </li>
            <li>
                <CicSelect
                    id="link_agGroups"
                    :enabled="editing"
                    :value="item.agentGroupIds"
                    :options="groups.map((group) => ({ value: group.id, label: group.name }))"
                    multiple
                    @input="(val) => updateFieldValue('agentGroupIds', val)"
                />
            </li>

            <li class="form-label">
                {{ $t('Assigned Queues') }}
            </li>
            <li>
                <CicSelect
                    id="link_queues"
                    :enabled="editing"
                    :value="item.queueIds"
                    :options="queues.map((queue) => ({ value: queue.queueId, label: queue.name }))"
                    multiple
                    @input="(val) => updateFieldValue('queueIds', val)"
                />
            </li>

            <li v-if="item.templateType === 'SummarisationTemplate'" id="language-label" class="form-label">
                {{ $t('Language') }}
            </li>
            <li v-if="item.templateType === 'SummarisationTemplate'">
                <CicSelect
                    id="language"
                    :enabled="editing"
                    :value="item.language"
                    :options="languages"
                    @input="(val) => updateFieldValue('language', val)"
                />
            </li>

            <li class="form-label">
                {{ $t('Template Type') }}
            </li>
            <li>
                <CicSelect
                    id="templateType"
                    :enabled="!item.templateId && editing"
                    :value="item.templateType"
                    :options="templateTypeOptions"
                    @input="(val) => updateFieldValue('templateType', val)"
                />
            </li>

            <div v-if="item.templateType === 'SummarisationTemplate'">
                <li class="form-label">
                    {{ $t('Summarisation Tasks') }}
                </li>
                <li>
                    <table id="summary_table" class="cic-table">
                        <thead>
                            <tr>
                                <th class="goal-column">{{ $t('Goal Description') }}</th>
                                <th class="anonymise-column">{{ $t('Anonymise') }}</th>
                                <th class="style-column">{{ $t('Style') }}</th>
                                <th class="word-limit-column">{{ $t('Word Limit') }}</th>
                                <th v-if="editing" class="action-column">&nbsp;</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr v-for="(summaryItem, index) in item.summaryItems" :key="index">
                                <td>
                                    <cic-textarea
                                        :id="'goal_' + index"
                                        :enabled="editing"
                                        :value="summaryItem.goal"
                                        :maxlength="1024"
                                        @input="(val) => updateSummaryItem(index, 'goal', val)"
                                    />
                                </td>
                                <td class="center-content">
                                    <cic-checkbox
                                        :id="'anonymisationCheckbox_' + index"
                                        :enabled="editing"
                                        :value="summaryItem.anonymisation"
                                        @input="(val) => updateSummaryItem(index, 'anonymisation', val)"
                                    />
                                </td>
                                <td>
                                    <CicSelect
                                        :id="'styleOptions_' + index"
                                        :enabled="editing"
                                        :value="summaryItem.style"
                                        :options="styles"
                                        @input="(val) => updateSummaryItem(index, 'style', val)"
                                    />
                                </td>
                                <td>
                                    <CicNumber
                                        :id="'wordLimit_' + index"
                                        :enabled="editing"
                                        :value="summaryItem.wordLimit"
                                        :min="1"
                                        :max="1000"
                                        @input="(val) => updateSummaryItem(index, 'wordLimit', val)"
                                    />
                                </td>
                                <td v-if="editing" style="padding: 0">
                                    <icon-button
                                        v-if="item.summaryItems.length > 1"
                                        icon="remove-icon"
                                        @click="removeSummaryItem(index)"
                                    />
                                </td>
                            </tr>
                            <tr v-if="editing && item.summaryItems.length < summaryItemLimit">
                                <td colspan="4"></td>
                                <td style="padding: 0">
                                    <icon-button id="add_summary_item" icon="add-icon" @click="addSummaryItem" />
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </li>

                <li class="form-label">
                    {{ $t('Custom Vocabularies') }}
                </li>
                <li>
                    <table id="vocab_table" class="cic-table">
                        <thead>
                            <tr>
                                <th class="vocab-column">{{ $t('Word') }}</th>
                                <th class="vocab-column">{{ $t('Definition') }}</th>
                                <th v-if="editing" class="action-column">&nbsp;</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr v-for="(vocab, index) in item.customVocabularies" :key="index">
                                <td>
                                    <cic-textbox
                                        :id="'Keyword_' + index"
                                        :enabled="editing"
                                        :value="vocab.key"
                                        :maxlength="256"
                                        @input="(val) => updateCustomVocab(index, 'key', val)"
                                    />
                                </td>
                                <td>
                                    <cic-textarea
                                        :id="'Definition_' + index"
                                        :enabled="editing"
                                        :value="vocab.value"
                                        :maxlength="512"
                                        @input="(val) => updateCustomVocab(index, 'value', val)"
                                    />
                                </td>
                                <td v-if="editing" style="padding: 0">
                                    <icon-button icon="remove-icon" @click="removeCustomVocab(index)" />
                                </td>
                            </tr>
                            <tr v-if="editing && item.customVocabularies.length < vocabLimit">
                                <td colspan="2"></td>
                                <td style="padding: 0">
                                    <icon-button id="add_vocab" icon="add-icon" @click="addCustomVocab" />
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </li>
            </div>

            <div v-else-if="item.templateType === 'PromptTemplate'">
                <li class="form-label">
                    {{ $t('Custom Prompt') }}
                </li>
                <li>
                    <cic-textarea
                        id="customPrompt"
                        :enabled="editing"
                        :value="item.customPrompt"
                        :maxlength="8000"
                        @input="(val) => updateFieldValue('customPrompt', val)"
                    />
                </li>
            </div>
        </ul>
    </cic-popup>
</template>

<script>
    import CicSelect from '@/components/inputs/CicSelect';
    import CicTextbox from '@/components/inputs/CicTextbox';
    import CicTextarea from '@/components/inputs/CicTextarea';
    import CicCheckbox from '@/components/inputs/CicCheckbox';
    import CicNumber from '@/components/inputs/CicNumber';
    import CicPopup from '@/components/CicPopup';
    import IconButton from '@/components/controls/IconButton';

    export default {
        name: 'EditTemplateDefinition',
        components: {
            CicNumber,
            CicTextbox,
            CicCheckbox,
            CicPopup,
            CicSelect,
            CicTextarea,
            IconButton,
        },
        props: {
            item: {
                type: Object,
                required: true,
            },
            queues: {
                type: Array,
                required: true,
            },
            groups: {
                type: Array,
                required: true,
            },
            languages: {
                type: Array,
                required: true,
            },
        },
        emits: ['blur', 'input', 'change'],
        data() {
            return {
                styles: [
                    { value: 'bp', label: this.$t('Bulletpoints') },
                    { value: 'pt', label: this.$t('Plaintext') },
                    { value: 'kw', label: this.$t('Keywords') },
                    { value: 'yn', label: this.$t('Yes/No') },
                ],
                templateTypeOptions: [
                    { value: 'SummarisationTemplate', label: this.$t('Summarisation Templates') },
                    { value: 'PromptTemplate', label: this.$t('Custom Prompts') },
                ],
                summaryItemLimit: 10,
                vocabLimit: 10,
                editing: false,
            };
        },
        created() {
            this.editing = !this.item.templateId || this.$store.hasRight('EDIT_SUMMARISATION_TEMPLATES');
        },
        methods: {
            addSummaryItem() {
                const updatedSummaryItems = [
                    ...this.item.summaryItems,
                    {
                        goal: '',
                        style: 'bp',
                        anonymisation: false,
                        wordLimit: 200,
                    },
                ];
                this.updateFieldValue('summaryItems', updatedSummaryItems);
            },

            updateSummaryItem(index, field, value) {
                const updatedSummaryItems = [...this.item.summaryItems];
                updatedSummaryItems[index][field] = value;
                this.updateFieldValue('summaryItems', updatedSummaryItems);
            },

            removeSummaryItem(index) {
                const updatedSummaryItems = [...this.item.summaryItems];
                updatedSummaryItems.splice(index, 1);
                this.updateFieldValue('summaryItems', updatedSummaryItems);
            },

            updateFieldValue(field, value) {
                this.$emit('change', field, value);
            },

            addCustomVocab() {
                const updatedVocabularies = [...this.item.customVocabularies, { key: '', value: '' }];
                this.updateFieldValue('customVocabularies', updatedVocabularies);
            },

            updateCustomVocab(index, field, value) {
                const updatedVocabularies = [...this.item.customVocabularies];
                updatedVocabularies[index][field] = value;
                this.updateFieldValue('customVocabularies', updatedVocabularies);
            },

            removeCustomVocab(index) {
                const updatedVocabularies = [...this.item.customVocabularies];
                updatedVocabularies.splice(index, 1);
                this.updateFieldValue('customVocabularies', updatedVocabularies);
            },
        },
    };
</script>
